import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { API_URL } from '../config/api';

const PassowordRest = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [passwordRulesVisible, setPasswordRulesVisible] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear confirm password error when new password changes
    if (name === 'newPassword' && errors.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  };

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');

    if (!tokenParam || !emailParam) {
      toast.error('Invalid reset link. Missing token or email.');
      setErrors(prev => ({ ...prev, token: 'Invalid reset link. Missing token or email.' }));
      return;
    }

    setToken(tokenParam);
    setEmail(decodeURIComponent(emailParam));
  }, [searchParams]);

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      errors: {
        minLength: !minLength,
        hasUpperCase: !hasUpperCase,
        hasLowerCase: !hasLowerCase,
        hasNumber: !hasNumber,
        hasSpecialChar: !hasSpecialChar
      }
    };
  };

  const passwordValidation = formData.newPassword ? validatePassword(formData.newPassword) : null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else {
      const validation = validatePassword(formData.newPassword);
      if (!validation.isValid) {
        newErrors.newPassword = 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character';
      }
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    if (!token || !email) {
      toast.error('Invalid reset link. Missing token or email.');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        action: 'reset-employee-password',
        email: email,
        token: token,
        newPassword: formData.newPassword
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success('Password reset successfully.');
        setIsSubmitted(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const errorMessage = result?.message || 'Failed to reset password. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="login-wrapper">
        <div className="login-card">
          {/* Left Side - Quotes */}
          <div className="login-left">
            <div className="login-left-content">
              <div className="quote-section">
                <h1 className="quote-title1">InstaTenders HR Portal!</h1>
                <p className="quote-text">
                  தன் உளரின் ஊறிய வாற்றல் பிறர்நூறியும்
                  ஊறிய தில்லை பகை.
                </p>
                <p className="quote-author">— திருவள்ளுவர் </p>
              </div>
            </div>
          </div>

          {/* Right Side - Reset Password Form */}
          <div className="login-right">
            <div className="login-body">
              {/* Logo Section */}
              <div className="logo-container-right">
                <img
                  src="/assets/img/Website_logo_color logo-black text-1.png"
                  alt="Company Logo"
                  className="login-logo-right"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="logo-placeholder-right" style={{ display: 'none' }}>
                  <i className="fas fa-building"></i>
                </div>
              </div>

              {!isSubmitted ? (
                <>
                  <form onSubmit={handleSubmit} noValidate>
                    {/* New Password Field */}
                    <div className="form-group">
                      <label htmlFor="newPassword" className="form-label">
                        New Password
                      </label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <i className="fas fa-lock"></i>
                        </div>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword"
                          name="newPassword"
                          className={`form-input ${errors.newPassword ? 'error' : ''}`}
                          placeholder="Enter new password"
                          value={formData.newPassword}
                          onChange={(e) => {
                            handleChange(e);
                            setPasswordRulesVisible(true);
                          }}
                          onFocus={() => setPasswordRulesVisible(true)}
                          onBlur={() => {
                            // hide rules only if password is valid
                            if (passwordValidation?.isValid) {
                              setPasswordRulesVisible(false);
                            }
                          }}
                          autoComplete="new-password"
                          aria-invalid={errors.newPassword ? 'true' : 'false'}
                          aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </button>
                      </div>
                      {errors.newPassword && (
                        <span className="error-message" id="newPassword-error" role="alert">
                          <i className="fas fa-exclamation-circle"></i> {errors.newPassword}
                        </span>
                      )}

                      {passwordValidation && (
                        <div
                          className="password-rules-wrapper"
                          style={{
                            marginTop: '8px',
                            fontSize: '0.9rem',
                            display: passwordRulesVisible ? 'block' : 'none'
                          }}
                        >
                          <strong className={passwordValidation.isValid }>
                            Password must contain:
                          </strong>
                          <ul className="mb-0" style={{ paddingLeft: '1.2rem', marginTop: '0.4rem' }}>
                            <li className={passwordValidation.errors.minLength ? 'text-danger' : 'text-success'}>
                              At least 8 characters
                            </li>
                            <li className={passwordValidation.errors.hasUpperCase ? 'text-danger' : 'text-success'}>
                              One uppercase letter
                            </li>
                            <li className={passwordValidation.errors.hasLowerCase ? 'text-danger' : 'text-success'}>
                              One lowercase letter
                            </li>
                            <li className={passwordValidation.errors.hasNumber ? 'text-danger' : 'text-success'}>
                              One number
                            </li>
                            <li className={passwordValidation.errors.hasSpecialChar ? 'text-danger' : 'text-success'}>
                              One special character
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="form-group">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm Password
                      </label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <i className="fas fa-lock"></i>
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                          placeholder="Confirm new password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                          aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <span className="error-message" id="confirmPassword-error" role="alert">
                          <i className="fas fa-exclamation-circle"></i> {errors.confirmPassword}
                        </span>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className={`login-button ${isLoading ? 'loading' : ''}`}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Resetting...</span>
                        </>
                      ) : (
                        <>
                          <span>Reset Password</span>
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Login */}
                  <div className="form-options" style={{ marginTop: '24px', justifyContent: 'center' }}>
                    <Link to="/admin" className="forgot-link">
                      Back to Login
                    </Link>
                  </div>
                </>
              ) : (
                <div className="success-message">
                  <div className="success-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <h2 className="form-title">Password Reset Successful</h2>
                  <p className="form-subtitle">
                    Your password has been reset successfully.
                  </p>
                  <p className="form-subtitle" style={{ marginTop: '16px' }}>
                    Redirecting to login page...
                  </p>
                  <div className="form-options" style={{ marginTop: '32px', justifyContent: 'center' }}>
                    <Link to="/admin" className="forgot-link">
                      <i className="fas fa-arrow-left"></i> Back to Login
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassowordRest;
